import { MoreVertical, Plus, Pencil, Trash2 } from 'lucide-react';
import { DropdownMenu } from '../common/DropdownMenu';

interface EntryRowActionsProps {
  entryId: string;
  onAddBelow: (entryId: string) => void;
  onEdit: () => void;
  onDelete: (entryId: string) => void;
}

const BUTTON = 'flex items-center justify-center p-1 rounded text-gray-400';

// A timeline entry's row actions. From `sm` up they are three small icons,
// revealed on hover/keyboard focus via the row's `group`. On a phone three
// 44px targets would squeeze the entry's title to a word a line, so they are
// one "More" button (always visible — hover has no equivalent on touch).
export function EntryRowActions({ entryId, onAddBelow, onEdit, onDelete }: EntryRowActionsProps) {
  return (
    <>
      <div className="sm:hidden">
        <DropdownMenu
          align="end"
          ariaLabel="Entry actions"
          menuClassName="w-48"
          items={[
            {
              id: 'add',
              label: 'Add entry after',
              icon: <Plus size={15} />,
              onSelect: () => onAddBelow(entryId),
            },
            { id: 'edit', label: 'Edit', icon: <Pencil size={15} />, onSelect: onEdit },
            {
              id: 'delete',
              label: <span className="text-red-600">Delete</span>,
              icon: <Trash2 size={15} className="text-red-600" />,
              onSelect: () => onDelete(entryId),
              dividerBefore: true,
            },
          ]}
          trigger={(props) => (
            <button
              type="button"
              {...props}
              className="flex items-center justify-center min-h-11 min-w-11 rounded text-gray-400 hover:text-gray-600"
              aria-label="Entry actions"
              data-testid={`entry-menu-${entryId}`}
            >
              <MoreVertical size={16} />
            </button>
          )}
        />
      </div>
      <div className="hidden sm:flex items-center gap-0.5 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity shrink-0">
        <button
          type="button"
          onClick={() => onAddBelow(entryId)}
          className={`${BUTTON} hover:text-primary-600 hover:bg-primary-50`}
          aria-label="Add entry after this one"
          title="Add entry after this one"
          data-testid={`add-below-entry-${entryId}`}
        >
          <Plus size={14} />
        </button>
        <button
          type="button"
          onClick={() => onEdit()}
          className={`${BUTTON} hover:text-primary-600 hover:bg-primary-50`}
          aria-label="Edit entry"
          title="Edit entry"
          data-testid={`edit-entry-${entryId}`}
        >
          <Pencil size={14} />
        </button>
        <button
          type="button"
          onClick={() => onDelete(entryId)}
          className={`${BUTTON} hover:text-red-600 hover:bg-red-50`}
          aria-label="Delete entry"
          title="Delete entry"
          data-testid={`delete-entry-${entryId}`}
        >
          <Trash2 size={14} />
        </button>
      </div>
    </>
  );
}
